/**
 * The one place a table gets inserted, shared by every entry point (the
 * toolbar's Insert table item, the wiki slash menu, and any future size
 * picker) so the nesting rule can't be enforced in one of them and
 * forgotten in the next.
 */

import type { Editor } from '@tiptap/react'

/** Shape of a table created from a bare "insert a table" intent. */
export const DEFAULT_TABLE_ROWS = 3
export const DEFAULT_TABLE_COLS = 3

/**
 * Insert a default table, unless the caret already sits inside one.
 *
 * A cell cannot hold a table (see the content model in
 * `table-extensions.ts`), so ProseMirror's fitter has nowhere legal to
 * put the new node: it closes the cell, the row and the table, and drops
 * the new table AFTER the outer one. The author asked for a table inside
 * a cell and got their existing table torn in two around a third one
 * somewhere else — silently, with nothing to undo but the whole action.
 * Refusing is the honest answer, and it is what the structural menus can
 * actually express.
 *
 * Returns whether a table was inserted, so a caller with a live region
 * can announce the refusal rather than leaving the key press unexplained.
 */
export function insertDefaultTable(editor: Editor): boolean {
  if (editor.isActive('table')) return false
  return editor
    .chain()
    .focus()
    .insertTable({ rows: DEFAULT_TABLE_ROWS, cols: DEFAULT_TABLE_COLS, withHeaderRow: true })
    .run()
}
